// --- UPDATE (FlowSense AI Explainer + real-data pass)
//   • Chart text colour fix: ApexCharts was given the Chakra token 'gray.800' (not a CSS colour),
//     so axis labels fell back to defaults and were unreadable in dark mode.
//   • Priorities outside Critical/High/Medium/Low (e.g. "emergency", "urgent") were silently
//     dropped from the total. "emergency"/"urgent" now count as Critical, anything else is counted
//     and shown as "other" so the total matches the database.
//   • Live: refetches on work order changes (work_order_events), debounced. Selected card is
//     readable in dark mode.
//   • "Explain the priority mix" FlowSense AI explainer.
// --- END UPDATE
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Heading,
  Spinner,
  Flex,
  Text,
  useColorModeValue,
  Button,
  HStack,
  VStack,
  Badge,
  IconButton,
  Tooltip,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Switch,
  FormControl,
  FormLabel,
  Collapse,
  Code,
  Divider,
  Progress,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  SimpleGrid,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Select,
} from '@chakra-ui/react';
import ReactApexChart from 'react-apexcharts';
import {
  MdRefresh,
  MdDownload,
  MdSettings,
  MdBugReport,
  MdFullscreen,
  MdFullscreenExit,
} from 'react-icons/md';
import { FaChartBar, FaChartLine, FaChartPie } from 'react-icons/fa';
import Card from '../card/Cards';

// --- CRITICAL UPDATE: Swap to Analytics Service to prevent 10k data dump ---
import analyticsService from '../../services/analyticsService'; 
import FlowSenseAIExplainer from '../ai/FlowSenseAIExplainer'; // --- UPDATE
import useLiveRefresh from '../../hooks/useLiveRefresh';       // --- UPDATE

const WorkOrderByPriorityChart = ({ 
  data: externalData, 
  isLoading: externalLoading, 
  onPriorityClick,
  enableDebug = false,
  enableExport = true,
  enableComparison = false,
  historicalData = null,
}) => {
  // Theme
  const chartTextColor = useColorModeValue('#1A202C', '#FFFFFF'); // --- UPDATE: Apex needs a real CSS colour
  const selectedBg = useColorModeValue('blue.50', 'whiteAlpha.100'); // --- UPDATE

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const debugBg = useColorModeValue('gray.50', 'gray.700');

  // State
  const [chartType, setChartType] = useState('bar');
  const [showDebug, setShowDebug] = useState(enableDebug);
  const [debugLogs, setDebugLogs] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [selectedPriority, setSelectedPriority] = useState(null);
  const [chartOrientation, setChartOrientation] = useState('horizontal');
  const [showDataLabels, setShowDataLabels] = useState(true);
  const [showPercentages, setShowPercentages] = useState(false);
  const [performanceMetrics, setPerformanceMetrics] = useState({
    renderTime: 0,
    dataPoints: 0,
    lastUpdate: null,
  });

  // 🚀 ADVANCED DEBUGGING: Render Tracking
  const renderCount = useRef(0);
  renderCount.current += 1;

  // 🚀 SMART COMPONENT STATE: Internal data fetching
  const [internalData, setInternalData] = useState(null);
  const [internalLoading, setInternalLoading] = useState(false);

  // Determine active data source (Props override internal state if provided)
  const activeData = externalData || internalData;
  const activeLoading = externalLoading !== undefined ? externalLoading : internalLoading;

  const { isOpen: isSettingsOpen, onOpen: onSettingsOpen, onClose: onSettingsClose } = useDisclosure();
  const toast = useToast();

  // 🚀 ADVANCED DEBUGGING: Enhanced Debug Logger
  const addDebugLog = useCallback((message, type = 'info', details = null) => {
    if (!enableDebug && !showDebug) return;
    
    const timestamp = new Date().toISOString();
    
    // Safely parse details to prevent React state/render crashes with circular JSON or massive DOM objects
    let safeDetails = details;
    try {
      if (details instanceof Error) {
        safeDetails = { message: details.message, name: details.name, stack: details.stack };
      } else if (typeof details === 'object' && details !== null) {
        // Shallow clone to strip non-serializable elements
        safeDetails = JSON.parse(JSON.stringify(details, (key, value) => 
          typeof value === 'function' ? '[Function]' : value
        ));
      }
    } catch(e) {
      safeDetails = "[Unserializable Object - See Browser Console]";
    }

    const log = { timestamp, type, message, details: safeDetails };
    
    // Increased buffer to 100 for deep trace analysis
    setDebugLogs(prev => [log, ...prev].slice(0, 100));
    
    if (type === 'error') {
      console.error(`[WorkOrderChart] ❌ ${message}`, details);
      console.trace(); // Forces a stack trace printout in the browser console
    } else if (type === 'warn') {
      console.warn(`[WorkOrderChart] ⚠️ ${message}`, details);
    } else {
      console.log(`[WorkOrderChart] ℹ️ ${message}`, details);
    }
  }, [enableDebug, showDebug]);

  // 🚀 ADVANCED DEBUGGING: Lifecycle Monitors
  // These discrete hooks map out exactly what data is flowing into the component
  useEffect(() => {
    if (renderCount.current % 5 === 0) {
       addDebugLog(`Component Rendered Cycle (Count: ${renderCount.current})`, 'info', { chartType, activeLoading });
    }
  });

  useEffect(() => {
    addDebugLog('Input Data Source Changed', 'info', {
      hasExternalData: !!externalData,
      internalDataExists: !!internalData,
      activeDataIsArray: Array.isArray(activeData),
      activeDataSize: activeData ? (Array.isArray(activeData) ? activeData.length : Object.keys(activeData).length) : 0,
      sampleHead: activeData && Array.isArray(activeData) ? activeData.slice(0, 2) : 'Not an Array'
    });
  }, [activeData, externalData, internalData, addDebugLog]);

  // 🚀 CRITICAL UPDATE: Fetch Aggregated Data directly from FlowSense Backend
  const fetchChartData = useCallback(async (forceRefresh = false) => {
    // If parent is handling data, don't fetch internally
    if (externalData) {
       addDebugLog(`fetchChartData skipped: externalData prop is managing state`, 'warn');
       return;
    }

    setInternalLoading(true);
    const startTime = performance.now();
    addDebugLog(`Initiating data fetch (forceRefresh: ${forceRefresh})`, 'info');

    try {
      // ENTERPRISE FIX: Use the analytics widget endpoint so the MySQL database 
      // does the heavy counting. Safely attempt both common analytics service patterns.
      let response;
      if (typeof analyticsService.getWorkOrderByPriority === 'function') {
         response = await analyticsService.getWorkOrderByPriority();
      } else if (typeof analyticsService.getWidgetData === 'function') {
         response = await analyticsService.getWidgetData('work-orders-by-priority');
      } else {
         throw new Error('Analytics service method not found.');
      }
      
      const parsedData = response?.data?.data || response?.data || response || [];
      addDebugLog(`Raw payload received from API`, 'info', { payloadType: typeof parsedData, isArray: Array.isArray(parsedData) });

      setInternalData(parsedData);
      
      const fetchTime = performance.now() - startTime;
      addDebugLog(`Aggregated data fetched successfully`, 'success', { ms: fetchTime.toFixed(2) });

      if (forceRefresh) {
        toast({
          title: 'Data Refreshed',
          description: 'Synced priority analytics from database.',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      }
    } catch (error) {
      addDebugLog(`Data fetch failed: ${error.message}`, 'error', error);
      toast({
        title: 'Error fetching chart data',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setInternalLoading(false);
    }
  }, [externalData, addDebugLog, toast]);

  // Initial load
  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);
  useLiveRefresh(['work_order_events'], () => fetchChartData(false), { enabled: !externalData, accept: (b) => !b?.type || ['created', 'updated', 'status_updated', 'assigned', 'completed', 'closed', 'deleted', 'priority_changed'].includes(b.type) }); // --- UPDATE

  // --- STRICT NORMALIZER: Prevents crashes if parent passes wrong widget data ---
  const normalizedData = useMemo(() => {
    if (!activeData) {
       addDebugLog(`NormalizedData aborted: activeData is falsy`, 'warn');
       return null;
    }

    let iterableData = [];
    if (Array.isArray(activeData)) {
      iterableData = activeData;
    } else if (typeof activeData === 'object') {
      iterableData = Object.entries(activeData).map(([k, v]) => ({ priority: k, count: v }));
    } else {
      addDebugLog(`NormalizedData failed: activeData is neither Array nor Object`, 'error', { typeof: typeof activeData });
      return null;
    }

    // Force exactly 4 strict priority buckets to prevent UI breaks
    const counts = { Critical: 0, High: 0, Medium: 0, Low: 0 };
    
    iterableData.forEach(item => {
      const key = String(item.priority || item.type || item.name || '').toLowerCase();
      const n = Number(item.count) || 0;
      if (key.includes('critical') || key.includes('emergency') || key.includes('urgent')) counts.Critical += n; // --- UPDATE
      else if (key.includes('high')) counts.High += n;
      else if (key.includes('medium') || key.includes('normal')) counts.Medium += n; // --- UPDATE
      else if (key.includes('low')) counts.Low += n;
    });

    const result = [
      { priority: 'Critical', count: counts.Critical },
      { priority: 'High', count: counts.High },
      { priority: 'Medium', count: counts.Medium },
      { priority: 'Low', count: counts.Low },
    ];

    return result;
  }, [activeData, addDebugLog]);

  // --- UPDATE: work orders whose priority label is none of the four buckets (counted, not dropped)
  const otherCount = useMemo(() => {
    if (!activeData) return 0;
    const rows = Array.isArray(activeData) ? activeData
      : (typeof activeData === 'object' ? Object.entries(activeData).map(([k, v]) => ({ priority: k, count: v })) : []);
    return rows.reduce((s, item) => {
      const key = String(item.priority || item.type || item.name || '').toLowerCase();
      return /critical|emergency|urgent|high|medium|normal|low/.test(key) ? s : s + (Number(item.count) || 0);
    }, 0);
  }, [activeData]);
  // --- END UPDATE

  // 🚀 ADVANCED DEBUGGING: Trace Normalizer Output
  useEffect(() => {
    addDebugLog('Normalized Data Evaluated', 'success', { output: normalizedData });
  }, [normalizedData, addDebugLog]);

  // Data Processing
  const processedData = useMemo(() => {
    const startTime = performance.now();
    
    try {
      if (!normalizedData || normalizedData.length === 0) {
        addDebugLog(`ProcessedData aborted: normalizedData is empty or null`, 'warn');
        return null;
      }

      const processed = {
        series: normalizedData.map(item => item.count),
        labels: normalizedData.map(item => item.priority),
        raw: normalizedData,
        total: normalizedData.reduce((sum, item) => sum + item.count, 0),
      };

      // Calculate percentages
      processed.percentages = processed.series.map(count => 
        processed.total === 0 ? "0.0" : ((count / processed.total) * 100).toFixed(1)
      );

      // Calculate trends if historical data is available
      if (historicalData && Array.isArray(historicalData)) {
        processed.trends = normalizedData.map((item, index) => {
          const historical = historicalData.find(h => h.priority === item.priority);
          if (historical && historical.count > 0) {
            const change = ((item.count - historical.count) / historical.count) * 100;
            return {
              direction: change > 0 ? 'increase' : change < 0 ? 'decrease' : 'unchanged',
              percentage: Math.abs(change).toFixed(1),
            };
          }
          return null;
        });
      }

      const renderTime = performance.now() - startTime;
      
      setPerformanceMetrics({
        renderTime: renderTime.toFixed(2),
        dataPoints: normalizedData.length,
        lastUpdate: new Date(),
      });

      return processed;
    } catch (error) {
      addDebugLog(`Error processing data: ${error.message}`, 'error', error);
      return null;
    }
  }, [normalizedData, historicalData, addDebugLog]);

  // 🚀 ADVANCED DEBUGGING: Trace Processed Output
  useEffect(() => {
    addDebugLog('Processed Data Evaluated', 'success', { 
      total: processedData?.total,
      series: processedData?.series,
      labels: processedData?.labels
    });
  }, [processedData, addDebugLog]);

  // Chart Configuration
  const chartConfig = useMemo(() => {
    if (!processedData) return null;

    const priorityColors = {
      Critical: '#E53E3E',
      High: '#FF4560',
      Medium: '#FFB900',
      Low: '#00E396',
    };

    const chartColors = processedData.labels.map(
      label => priorityColors[label] || '#808080'
    );

    const baseOptions = {
      chart: {
        type: chartType === 'pie' ? 'pie' : chartType === 'line' ? 'line' : 'bar',
        foreColor: chartTextColor,
        toolbar: {
          show: true,
          tools: {
            download: enableExport,
            selection: false,
            zoom: chartType !== 'pie',
            zoomin: chartType !== 'pie',
            zoomout: chartType !== 'pie',
            pan: false,
            reset: chartType !== 'pie',
          },
        },
        events: {
          dataPointSelection: (event, chartContext, config) => {
            // FIX: Safely extract index, guarding against chart background clicks
            const index = config.dataPointIndex !== undefined && config.dataPointIndex !== -1 
                ? config.dataPointIndex 
                : config.seriesIndex;
            
            if (index === undefined || index === -1) {
                addDebugLog('Chart clicked but no valid index returned (whitespace click)', 'warn', { config });
                return;
            }

            const selectedPriority = processedData.labels[index].toLowerCase();
            setSelectedPriority(selectedPriority);
            
            addDebugLog(`Priority data point selected: ${selectedPriority}`, 'info', {
              count: processedData.series[index],
              percentage: processedData.percentages[index],
              rawIndex: index
            });

            if (onPriorityClick) {
              onPriorityClick(selectedPriority);
            }

            toast({
              title: 'Priority Selected',
              description: `Viewing ${selectedPriority} priority work orders`,
              status: 'info',
              duration: 2000,
              isClosable: true,
            });
          },
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
        },
      },
      colors: chartColors,
      labels: processedData.labels,
      dataLabels: {
        enabled: showDataLabels,
        style: {
          colors: chartType === 'pie' ? ['#fff'] : ['#333'],
          fontSize: '14px',
          fontWeight: 600,
        },
        offsetX: chartType === 'bar' && chartOrientation === 'horizontal' ? 15 : 0,
        formatter: (val, opts) => {
          // FIX: Correctly map index for Pie vs Bar charts
          const index = chartType === 'pie' ? opts.seriesIndex : opts.dataPointIndex;
          if (chartType === 'pie') {
            return showPercentages ? `${val.toFixed(1)}%` : processedData.series[index];
          }
          return showPercentages 
            ? `${processedData.percentages[index]}%`
            : `${val}`;
        },
      },
      tooltip: {
        enabled: true,
        theme: 'dark',
        y: {
          formatter: (val, opts) => {
            // FIX: Correctly map index for Tooltips
            const index = chartType === 'pie' ? opts.seriesIndex : opts.dataPointIndex;
            const percentage = processedData.percentages[index];
            return `${val} Work Orders (${percentage}%)`;
          },
        },
      },
      grid: {
        borderColor: '#424242',
        strokeDashArray: 4,
        padding: { right: 20, left: 20 },
      },
    };

    if (chartType === 'bar') {
      return {
        ...baseOptions,
        plotOptions: {
          bar: {
            horizontal: chartOrientation === 'horizontal',
            distributed: true,
            barHeight: chartOrientation === 'horizontal' ? '60%' : '70%',
            borderRadius: 4,
            dataLabels: { position: 'top' },
          },
        },
        xaxis: chartOrientation === 'horizontal' ? {
          categories: processedData.labels,
          labels: { style: { fontSize: '12px' } },
          title: { text: 'Total Work Orders', style: { fontSize: '14px', fontWeight: 600 } },
        } : {
          categories: processedData.labels,
          labels: { style: { fontSize: '12px', fontWeight: 600 } },
        },
        yaxis: chartOrientation === 'horizontal' ? {
          labels: { style: { fontSize: '14px', fontWeight: 600 } },
        } : {
          title: { text: 'Total Work Orders', style: { fontSize: '14px', fontWeight: 600 } },
        },
        legend: { show: false },
        title: {
          text: 'Click on a bar to filter by priority',
          align: 'center',
          style: { fontSize: '12px', color: '#888' },
        },
      };
    }

    if (chartType === 'pie') {
      return {
        ...baseOptions,
        legend: {
          show: true,
          position: 'bottom',
          fontSize: '14px',
          labels: { colors: chartTextColor },
        },
        responsive: [{
          breakpoint: 480,
          options: { legend: { position: 'bottom' } }
        }],
      };
    }

    if (chartType === 'line') {
      return {
        ...baseOptions,
        stroke: { curve: 'smooth', width: 3 },
        markers: { size: 6, hover: { size: 8 } },
        xaxis: {
          categories: processedData.labels,
          labels: { style: { fontSize: '12px', fontWeight: 600 } },
        },
        yaxis: {
          title: { text: 'Work Orders', style: { fontSize: '14px', fontWeight: 600 } },
        },
        legend: { show: false },
      };
    }

    return baseOptions;
  }, [
    processedData, chartType, chartOrientation, showDataLabels,
    showPercentages, chartTextColor, enableExport, onPriorityClick,
    addDebugLog, toast,
  ]);

  const chartSeries = useMemo(() => {
    if (!processedData) return [];
    if (chartType === 'pie') return processedData.series;
    return [{ name: 'Work Orders', data: processedData.series }];
  }, [processedData, chartType]);

  // --- UPDATE: FlowSense rule findings
  const prioExplainer = useMemo(() => {
    const findings = [];
    if (!processedData || !processedData.total) return { findings, facts: {} };
    const get = (l) => processedData.series[processedData.labels.indexOf(l)] || 0;
    const t = processedData.total;
    const crit = get('Critical'); const high = get('High');
    const urgentShare = ((crit + high) / t) * 100;
    if (crit > 0) findings.push({ severity: (crit / t) >= 0.2 ? 'high' : 'medium', text: `${crit} critical work orders — ${((crit / t) * 100).toFixed(0)}% of the total.` });
    findings.push({ severity: urgentShare >= 50 ? 'medium' : 'info', text: `${urgentShare.toFixed(0)}% of work orders are Critical or High.`, evidence: urgentShare >= 50 ? 'When most work is urgent, priority stops separating jobs.' : undefined });
    if (otherCount > 0) findings.push({ severity: 'info', text: `${otherCount} work orders carry another priority label and are not in the four bars.` });
    return { findings, facts: { total: t, other_priority_labels: otherCount, by_priority: processedData.labels.map((l, i) => ({ priority: l, count: processedData.series[i], share_pct: Number(processedData.percentages[i]) })), selected: selectedPriority } };
  }, [processedData, otherCount, selectedPriority]);
  // --- END UPDATE

  // Export functionality
  const handleExport = useCallback(() => {
    if (!processedData) return;

    try {
      const exportData = {
        timestamp: new Date().toISOString(),
        chartType,
        data: processedData.raw,
        summary: {
          total: processedData.total,
          byPriority: processedData.labels.map((label, index) => ({
            priority: label,
            count: processedData.series[index],
            percentage: processedData.percentages[index],
          })),
        },
      };

      const dataStr = JSON.stringify(exportData, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
      const exportFileDefaultName = `work-orders-by-priority-${Date.now()}.json`;
      
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();

      addDebugLog('Data exported successfully', 'success');
      
      toast({
        title: 'Export Complete',
        description: 'Work order data has been exported',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      addDebugLog(`Export failed: ${error.message}`, 'error', error);
      toast({
        title: 'Export Failed',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [processedData, chartType, addDebugLog, toast]);

  // Render functions
  const renderStats = () => {
    if (!processedData) return null;

    return (
      <SimpleGrid columns={{ base: 2, md: processedData.labels.length }} spacing={2} mb={4}>
        {processedData.labels.map((label, index) => {
          const trend = processedData.trends?.[index];
          return (
            <Stat
              key={label}
              size="sm"
              p={2}
              borderRadius="md"
              borderWidth="1px"
              borderColor={selectedPriority === label.toLowerCase() ? 'blue.500' : borderColor}
              bg={selectedPriority === label.toLowerCase() ? selectedBg : cardBg}
              cursor="pointer"
              onClick={() => {
                setSelectedPriority(label.toLowerCase());
                if (onPriorityClick) onPriorityClick(label.toLowerCase());
              }}
              transition="all 0.2s"
              _hover={{ borderColor: 'blue.400', transform: 'translateY(-2px)' }}
            >
              <StatLabel fontSize="xs">{label}</StatLabel>
              <StatNumber fontSize="lg">{processedData.series[index]}</StatNumber>
              <StatHelpText fontSize="xs">
                {processedData.percentages[index]}%
                {trend && (
                  <>
                    {' • '}
                    <StatArrow type={trend.direction === 'increase' ? 'increase' : 'decrease'} />
                    {trend.percentage}%
                  </>
                )}
              </StatHelpText>
            </Stat>
          );
        })}
      </SimpleGrid>
    );
  };

  const renderDebugConsole = () => (
    <Collapse in={showDebug} animateOpacity>
      <Box mt={4} p={3} bg={debugBg} borderRadius="md" borderWidth="1px" borderColor={borderColor} maxH="300px" overflowY="auto">
        <HStack justify="space-between" mb={2}>
          <Text fontWeight="bold" fontSize="sm">Advanced Debug Console</Text>
          <HStack>
             <Badge colorScheme="blue">Renders: {renderCount.current}</Badge>
             <Button size="xs" onClick={() => setDebugLogs([])}>Clear</Button>
          </HStack>
        </HStack>
        
        {/* Performance Metrics */}
        <Box mb={2} p={2} bg={cardBg} borderRadius="md">
          <Text fontSize="xs" fontWeight="bold" mb={1}>Performance Metrics:</Text>
          <SimpleGrid columns={3} spacing={2} fontSize="xs">
            <Box>
              <Text color="gray.500">Render Time:</Text>
              <Text fontWeight="semibold">{performanceMetrics.renderTime}ms</Text>
            </Box>
            <Box>
              <Text color="gray.500">Data Points:</Text>
              <Text fontWeight="semibold">{performanceMetrics.dataPoints}</Text>
            </Box>
            <Box>
              <Text color="gray.500">Last Update:</Text>
              <Text fontWeight="semibold">
                {performanceMetrics.lastUpdate?.toLocaleTimeString() || 'N/A'}
              </Text>
            </Box>
          </SimpleGrid>
        </Box>

        <Divider mb={2} />

        {/* Debug Logs */}
        <VStack align="stretch" spacing={2} fontSize="xs">
          {debugLogs.map((log, index) => (
            <Box 
               key={index} 
               p={2} 
               borderRadius="md" 
               bg={
                 log.type === 'error' ? 'red.50' :
                 log.type === 'warn' ? 'yellow.50' :
                 log.type === 'success' ? 'green.50' : 'white'
               }
               borderWidth="1px"
               borderColor={
                 log.type === 'error' ? 'red.200' :
                 log.type === 'warn' ? 'yellow.200' :
                 log.type === 'success' ? 'green.200' : 'gray.200'
               }
            >
              <Text fontWeight="bold" color={
                 log.type === 'error' ? 'red.700' :
                 log.type === 'warn' ? 'yellow.700' :
                 log.type === 'success' ? 'green.700' : 'gray.700'
              }>
                [{new Date(log.timestamp).toLocaleTimeString()}] {log.type.toUpperCase()}: {log.message}
              </Text>
              {log.details && (
                 <Code mt={1} p={1} w="100%" fontSize="10px" colorScheme="blackAlpha" whiteSpace="pre-wrap" display="block">
                    {JSON.stringify(log.details, null, 2)}
                 </Code>
              )}
            </Box>
          ))}
          {debugLogs.length === 0 && (
            <Text color="gray.500" textAlign="center">No logs yet</Text>
          )}
        </VStack>
      </Box>
    </Collapse>
  );

  const renderContent = () => {
    if (activeLoading) {
      return (
        <Flex justify="center" align="center" h="300px" direction="column">
          <Spinner size="xl" mb={4} color="blue.500" />
          <Text color="gray.500">Loading priority analytics...</Text>
          <Progress size="xs" isIndeterminate w="200px" mt={2} colorScheme="blue" />
        </Flex>
      );
    }

    if (!processedData || !Array.isArray(normalizedData) || processedData.total === 0) {
      return (
        <Flex justify="center" align="center" h="300px" direction="column">
          <Text color="gray.500" fontSize="lg" mb={2}>No work order data available</Text>
          <Text color="gray.400" fontSize="sm">No priorities to analyze.</Text>
        </Flex>
      );
    }

    return (
      <Box>
        {renderStats()}
        {otherCount > 0 && (
          <Text fontSize="xs" color="gray.500" mb={2}>+ {otherCount} with another priority label (not in the bars or the total)</Text>
        )}
        <ReactApexChart 
          options={chartConfig} 
          series={chartSeries} 
          type={chartType === 'pie' ? 'pie' : chartType === 'line' ? 'line' : 'bar'}
          height={isFullscreen ? 500 : 350} 
        />
        {/* --- UPDATE: FlowSense AI explainer */}
        <Box mt={4}>
          <FlowSenseAIExplainer
            module="work_orders_by_priority"
            title="Explain the priority mix"
            subtitle="How urgent the open work is"
            facts={prioExplainer.facts}
            findings={prioExplainer.findings}
            compact
            questions={['Are we over-using Critical priority?', 'Which priority band is hurting SLA most?']}
          />
        </Box>
        {/* --- END UPDATE */}
      </Box>
    );
  };

  return (
    <Card h="100%" position="relative">
      {/* Header */}
      <Flex justify="space-between" align="center" mb={4}>
        <HStack>
          <Heading size="md">Work Orders by Priority</Heading>
          {processedData && (
            <Badge colorScheme="blue" fontSize="sm">
              {processedData.total} Total
            </Badge>
          )}
        </HStack>

        <HStack spacing={2}>
          {/* 🚀 NEW: Refresh Data Button */}
          {!externalData && (
            <Tooltip label="Refresh Priority Data">
              <IconButton
                icon={<MdRefresh />}
                size="sm"
                variant="outline"
                isLoading={activeLoading}
                onClick={() => fetchChartData(true)}
              />
            </Tooltip>
          )}

          {/* Chart Type Selector */}
          <Menu>
            <Tooltip label="Chart type">
              <MenuButton as={IconButton} icon={<FaChartBar />} size="sm" variant="outline" />
            </Tooltip>
            <MenuList>
              <MenuItem icon={<FaChartBar />} onClick={() => setChartType('bar')}>
                Bar Chart
              </MenuItem>
              <MenuItem icon={<FaChartLine />} onClick={() => setChartType('line')}>
                Line Chart
              </MenuItem>
              <MenuItem icon={<FaChartPie />} onClick={() => setChartType('pie')}>
                Pie Chart
              </MenuItem>
            </MenuList>
          </Menu>

          {/* Export Button */}
          {enableExport && (
            <Tooltip label="Export data">
              <IconButton
                icon={<MdDownload />}
                size="sm"
                variant="outline"
                onClick={handleExport}
                isDisabled={!processedData || activeLoading}
              />
            </Tooltip>
          )}

          {/* Settings Button */}
          <Tooltip label="Chart settings">
            <IconButton icon={<MdSettings />} size="sm" variant="outline" onClick={onSettingsOpen} />
          </Tooltip>

          {/* Debug Toggle */}
          {enableDebug && (
            <Tooltip label="Toggle debug console">
              <IconButton
                icon={<MdBugReport />}
                size="sm"
                variant="outline"
                colorScheme={showDebug ? 'red' : 'gray'}
                onClick={() => setShowDebug(!showDebug)}
              />
            </Tooltip>
          )}

          {/* Fullscreen Toggle */}
          <Tooltip label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
            <IconButton
              icon={isFullscreen ? <MdFullscreenExit /> : <MdFullscreen />}
              size="sm"
              variant="outline"
              onClick={() => setIsFullscreen(!isFullscreen)}
            />
          </Tooltip>
        </HStack>
      </Flex>

      {/* Content */}
      {renderContent()}

      {/* Debug Console */}
      {enableDebug && renderDebugConsole()}

      {/* Settings Modal */}
      <Modal isOpen={isSettingsOpen} onClose={onSettingsClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Chart Settings</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm">Chart Type</FormLabel>
                <Select value={chartType} onChange={(e) => setChartType(e.target.value)}>
                  <option value="bar">Bar Chart</option>
                  <option value="line">Line Chart</option>
                  <option value="pie">Pie Chart</option>
                </Select>
              </FormControl>

              {chartType === 'bar' && (
                <FormControl>
                  <FormLabel fontSize="sm">Orientation</FormLabel>
                  <Select value={chartOrientation} onChange={(e) => setChartOrientation(e.target.value)}>
                    <option value="horizontal">Horizontal</option>
                    <option value="vertical">Vertical</option>
                  </Select>
                </FormControl>
              )}

              <FormControl display="flex" alignItems="center">
                <FormLabel fontSize="sm" mb={0}>Show Data Labels</FormLabel>
                <Switch isChecked={showDataLabels} onChange={(e) => setShowDataLabels(e.target.checked)} />
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel fontSize="sm" mb={0}>Show Percentages</FormLabel>
                <Switch isChecked={showPercentages} onChange={(e) => setShowPercentages(e.target.checked)} />
              </FormControl>

              {enableComparison && historicalData && (
                <FormControl display="flex" alignItems="center">
                  <FormLabel fontSize="sm" mb={0}>Show Comparison</FormLabel>
                  <Switch isChecked={showComparison} onChange={(e) => setShowComparison(e.target.checked)} />
                </FormControl>
              )}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Card>
  );
};

export default WorkOrderByPriorityChart;